/**
 * EBScript — the builder returned by createScript().
 *
 * Authors accumulate registerBefore/registerAfter calls on this object.
 * Backend scripts (backendSource) export it as `export default script`.
 * Frontend scripts (frontendSource) do not export — the scripts.js generator
 * handles installation by calling EBScripts.register(id, script) automatically.
 */
export class EBScript {
    beforeHandlers = [];
    afterHandlers = [];

    /**
     * Register a handler to run before the core function.
     * @param {string} triggerName - The trigger to handle (e.g. 'api.registration.submit')
     * @param {Function} fn - Async handler: async (ctx) => ctx
     * @param {object} [opts]
     * @param {number} [opts.priority=50] - Execution priority (lower = earlier)
     * @returns {EBScript} this (for chaining)
     */
    registerBefore(triggerName, fn, { priority = 50 } = {}) {
        this.beforeHandlers.push({ triggerName, fn, priority });
        return this;
    }

    /**
     * Register a handler to run after the core function.
     * @param {string} triggerName - The trigger to handle
     * @param {Function} fn - Async handler: async (ctx) => ctx
     * @param {object} [opts]
     * @param {number} [opts.priority=50] - Execution priority (lower = earlier)
     * @returns {EBScript} this (for chaining)
     */
    registerAfter(triggerName, fn, { priority = 50 } = {}) {
        this.afterHandlers.push({ triggerName, fn, priority });
        return this;
    }
}

/**
 * Create a new EBScript builder instance.
 *
 * This is the primary authoring API for both frontendSource and backendSource.
 * Authors only write the registerBefore/registerAfter calls — the generators
 * on each side inject the boilerplate automatically:
 *
 *   Backend engine injects:
 *     import { createScript } from '@eventbuilder-utils/scripts';
 *     const script = createScript();
 *     ... backendSource ...
 *     export default script;
 *
 *   Frontend generator (scripts.js endpoint) injects:
 *     ;(function(registrar) {
 *       const script = registrar.createScript();
 *       ... frontendSource ...
 *       registrar.register(id, script);
 *     })(window.__EB_SCRIPTS_SCOPE_REGISTRARS__[token]);
 *
 * @returns {EBScript}
 */
export function createScript() {
    return new EBScript();
}
